import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import ExpirationPicker from "./fields/ExpirationPicker";
import LeveragePicker from "./fields/LeveragePicker";
import TradeSidePicker from "./fields/TradeSidePicker";
import UnifiedPricePicker from "./fields/UnifiedPricePicker";
import EntryProtectionSelector from "./fields/EntryProtectionSelector";
import VolumePicker from "./fields/VolumePicker";
import { saveTradedCurrency } from "../../../utils/utils";
import {
  buildLiquidityCatchCommand,
  formatTradeContextLabel,
  formatTradePrice,
  hasSelectedCandleContext,
  LIQUIDITY_CATCH_CANDLE_LEVELS,
  LIQUIDITY_CATCH_EXPIRIES,
  liquidityCatchCandleTimestamp,
  normalizeEntryProtectionMode,
  normalizeLiquidityCatchTimeframe,
  resolveLiquidityCatchCandleLevel
} from "../../../trades/tradeUtils";

const CATCH_PERCENT_OPTIONS = [0.5, 1, 1.5, 2, 3, 5, 10];
const CATCH_TP_OPTIONS = [1, 1.5, 2, 2.5, 3, 5, 10, 20];

function CatchValuePicker({ label, value, options, onChange, allowHold = false }) {
  return (
    <Col md={12}>
      <Form.Group className="mb-3">
        <Form.Label>{label}</Form.Label>
        <div className="d-flex flex-wrap mb-2">
          {options.map((option) => (
            <Button
              key={option}
              variant={String(value) === String(option) ? "primary" : "outline-primary"}
              active={String(value) === String(option)}
              onClick={() => onChange(option)}
              className="me-1 mb-1"
              size="sm"
            >
              {option}%
            </Button>
          ))}
          {allowHold && (
            <Button
              variant={value === "HOLD" ? "primary" : "outline-primary"}
              active={value === "HOLD"}
              onClick={() => onChange("HOLD")}
              className="me-1 mb-1"
              size="sm"
            >
              HOLD
            </Button>
          )}
        </div>
        <Form.Control
          inputMode="decimal"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={allowHold ? "Percent or HOLD" : "Percent"}
        />
      </Form.Group>
    </Col>
  );
}

function LiquidityCatchTradeForm(props) {
  const savedCatch = useMemo(() => JSON.parse(localStorage.getItem("catchTradeData") || "{}"), []);
  const [baselineSelector, setBaselineSelector] = useState(null);
  const [baselineSource, setBaselineSource] = useState("MANUAL");
  const [stopLossPercent, setStopLossPercent] = useState(savedCatch.stopLossPercent || 1);
  const [tp1Percent, setTp1Percent] = useState(savedCatch.tp1Percent || 2);
  const [tp2Percent, setTp2Percent] = useState(savedCatch.tp2Percent || "HOLD");
  const [catchExpiry, setCatchExpiry] = useState(savedCatch.catchExpiry || 24);
  const [entryProtectionMode, setEntryProtectionMode] = useState(
    normalizeEntryProtectionMode(savedCatch.entryProtectionMode, savedCatch.protectEntry)
  );

  const catchTimeframe = normalizeLiquidityCatchTimeframe(props.interval);
  const selectedCandle = props.tradeData?.selectedCandle;
  const hasCandle = hasSelectedCandleContext(selectedCandle);
  const isCandleBaseline = baselineSource === "SELECTED_CANDLE";
  const candleBaseline = useMemo(() => resolveLiquidityCatchCandleLevel(
    selectedCandle,
    baselineSelector,
    props.tradeSide
  ), [baselineSelector, props.tradeSide, selectedCandle]);
  const baseline = isCandleBaseline ? candleBaseline : Number(props.price);

  useEffect(() => {
    if (!hasCandle && isCandleBaseline) {
      setBaselineSource("MANUAL");
      setBaselineSelector(null);
    }
  }, [hasCandle, isCandleBaseline]);

  useEffect(() => {
    if (baselineSelector && !(LIQUIDITY_CATCH_CANDLE_LEVELS[props.tradeSide] || []).includes(baselineSelector)) {
      setBaselineSource("MANUAL");
      setBaselineSelector(null);
    }
  }, [baselineSelector, props.tradeSide]);

  useEffect(() => {
    localStorage.setItem("catchTradeData", JSON.stringify({
      stopLossPercent,
      tp1Percent,
      tp2Percent,
      catchExpiry,
      entryProtectionMode
    }));
  }, [stopLossPercent, tp1Percent, tp2Percent, catchExpiry, entryProtectionMode]);

  const selectManualBaseline = (value) => {
    props.setPrice(value);
    setBaselineSource("MANUAL");
    setBaselineSelector(null);
  };

  const selectCandleBaseline = (selector) => {
    setBaselineSource("SELECTED_CANDLE");
    setBaselineSelector(selector);
  };

  const handleSubmit = () => {
    const command = buildLiquidityCatchCommand({
      symbol: props.currency,
      side: props.tradeSide,
      amountUsdt: props.volume,
      leverage: props.leverage,
      timeframe: catchTimeframe,
      baseline,
      stopLossPercent,
      tp1Percent,
      tp2Percent,
      expiresIn: catchExpiry,
      entryProtectionMode,
      baselineSource: isCandleBaseline ? "SELECTED_CANDLE" : "MANUAL",
      baselineSelector: isCandleBaseline ? baselineSelector : "PRICE",
      baselineCandleTimestamp: isCandleBaseline
        ? liquidityCatchCandleTimestamp(selectedCandle, props.tradeData?.date)
        : null
    });

    if (!command) {
      alert("Side, amount, leverage, baseline, stop loss, TP1 and expiry must be selected.");
      return;
    }

    props.handleSubmit(command);
    saveTradedCurrency(props.currency, props.tradeSide);
  };

  const contextLabel = formatTradeContextLabel({
    currency: props.currency,
    interval: props.interval,
    candleLabel: props.candle,
    selectedPrice: props.price
  });

  return (
    <Form className="pt-2 pb-2 details">
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3 details">
            <h4 style={{ color: "white" }}>{contextLabel}</h4>
            <div className="text-secondary">{catchTimeframe}, up to 3 attempts</div>
          </Form.Group>
        </Col>

        <Col md={12}>
          <TradeSidePicker tradeSide={props.tradeSide} setTradeSide={props.setTradeSide} />
        </Col>

        <Col md={12}>
          <VolumePicker volume={props.volume} setVolume={props.setVolume} />
        </Col>

        <Col md={12}>
          <LeveragePicker leverage={props.leverage} setLeverage={props.setLeverage} />
        </Col>

        <UnifiedPricePicker
          fieldLabel="Baseline"
          manualValue={props.price}
          selectedOption={baselineSelector}
          options={LIQUIDITY_CATCH_CANDLE_LEVELS[props.tradeSide] || []}
          isOptionDisabled={() => !hasCandle}
          onManualChange={selectManualBaseline}
          onOptionSelect={selectCandleBaseline}
          currency={props.currency}
          interval={props.interval}
          draw={props.draw}
          setBtnDisabled={props.setBtnDisabled}
        />

        {isCandleBaseline && (
          <Col md={12}>
            <div className="text-secondary mb-3">
              Baseline: {Number.isFinite(candleBaseline) ? formatTradePrice(candleBaseline) : "Candle data unavailable"}
            </div>
          </Col>
        )}

        <Col md={12}>
          <EntryProtectionSelector value={entryProtectionMode} onChange={setEntryProtectionMode} />
        </Col>

        <CatchValuePicker label="Stop Loss %" value={stopLossPercent} options={CATCH_PERCENT_OPTIONS} onChange={setStopLossPercent} />
        <CatchValuePicker label="Take Profit 1 %" value={tp1Percent} options={CATCH_TP_OPTIONS} onChange={setTp1Percent} />
        <CatchValuePicker label="Take Profit 2" value={tp2Percent} options={CATCH_TP_OPTIONS} onChange={setTp2Percent} allowHold />
        <Col md={12}>
          <ExpirationPicker
            expiresIn={catchExpiry}
            setExpiresIn={setCatchExpiry}
            options={LIQUIDITY_CATCH_EXPIRIES}
          />
        </Col>
      </Row>

      <Button className="w-100" variant={props.tradeSide === "SHORT" ? "danger" : "success"} onClick={handleSubmit}>
        Start {props.tradeSide === "SHORT" ? "Top" : "Bottom"} Catch
      </Button>
    </Form>
  );
}

export default LiquidityCatchTradeForm;
